import {
  centralOrganizationListSchema,
  centralSummarySchema,
  formatDecimal,
  ORGANIZATION_STATUS_LABELS,
  PROJECT_PHASE_LABELS,
  targetPeriodLabel,
  type CentralOrganization,
  type CentralSummary,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  FolderKanban,
  Gauge,
  ListChecks,
  Plus,
  Search,
  Target,
  TrendingUp,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth, useCan } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const PAGE_SIZE = 20;
/** Colunas da lista (as mesmas no cabeçalho e nas linhas). */
const ROW_COLUMNS = 'lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_7.5rem_6.5rem_11rem]';
const SEARCH_DEBOUNCE_MS = 300;
/** Mesmo limite da API (organizationListQuerySchema). */
const MAX_SEARCH = 100;

/** Status do projeto na lista (PRD §11.2; DEC-059): as cores do PRD, com o que significam. */
const STATUS_LABELS: Record<TrafficLight, string> = {
  green: 'Verde',
  yellow: 'Amarelo',
  red: 'Vermelho',
  blue: 'Azul · em diagnóstico',
  gray: 'Sem dados',
};

/**
 * Central de Empresas: as empresas clientes que o usuário Fluxus acompanha (PRD §11.2; T-145, DEC-059) — os
 * indicadores da carteira e a lista com projeto, consultor, fase, performance, status e as ações.
 */
export function CentralEmpresasPage() {
  const { client } = useAuth();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') ?? '').trim().slice(0, MAX_SEARCH);
  const page = Math.max(1, Number(params.get('pagina')) || 1);
  const [search, setSearch] = useState(q);
  const searchId = useId();

  // A busca vai para o endereço (compartilhável e mantida ao voltar) depois de uma pausa na digitação.
  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next) p.set('q', next);
          else p.delete('q');
          p.delete('pagina');
          return p;
        },
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, q, setParams]);

  const query = useQuery({
    queryKey: ['organizations', 'central', { q, page }],
    queryFn: ({ signal }) =>
      client.request('/organizations', {
        query: { q: q || undefined, page, pageSize: PAGE_SIZE },
        schema: centralOrganizationListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  const goTo = (target: number) =>
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (target > 1) p.set('pagina', String(target));
      else p.delete('pagina');
      return p;
    });

  const data = query.data;

  return (
    <PageContainer>
      <PageHeader
        title="Central de Empresas"
        description="As empresas clientes que você acompanha, com o projeto BSC em andamento e o consultor responsável."
        actions={
          can('central-empresas', 'create') ? (
            <Button asChild>
              <Link to="/empresas/nova">
                <Plus aria-hidden />
                Nova empresa
              </Link>
            </Button>
          ) : null
        }
      />

      <Summary />
      <div className="mt-6 max-w-md">
        <label htmlFor={searchId} className="sr-only">
          Buscar empresa
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={searchId}
            type="search"
            placeholder="Buscar por nome, razão social ou CNPJ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={MAX_SEARCH}
            className="pl-9"
          />
        </div>
      </div>

      <section aria-label="Empresas" className="mt-6">
        {query.isPending ? (
          <LoadingState rows={5} label="Carregando empresas" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
        ) : data && data.total === 0 ? (
          q ? (
            <EmptyState
              icon={Search}
              title="Nenhuma empresa encontrada"
              description={`Nenhuma empresa corresponde a "${q}". Confira a grafia ou busque pelo CNPJ.`}
              action={
                <Button variant="outline" onClick={() => setSearch('')}>
                  <X aria-hidden />
                  Limpar busca
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Building2}
              title="Nenhuma empresa cliente por aqui"
              description="As empresas aparecem nesta lista quando são cadastradas pela Fluxus e, para consultores, quando você é alocado nelas."
            />
          )
        ) : data && data.items.length === 0 ? (
          // Endereço com página além da última (ex.: a lista diminuiu desde que o link foi copiado).
          <EmptyState
            icon={Building2}
            title="Esta página não existe mais"
            description={`A lista tem ${data.totalPages} ${data.totalPages === 1 ? 'página' : 'páginas'}.`}
            action={
              <Button variant="outline" onClick={() => goTo(1)}>
                Ir para a primeira página
              </Button>
            }
          />
        ) : data ? (
          <>
            {/* Cabeçalho das colunas (no celular cada linha leva os rótulos; para leitores de tela, também). */}
            <div
              aria-hidden
              className={cn(
                'hidden px-4 pb-2 text-xs font-semibold text-muted-foreground lg:grid lg:gap-4 md:px-5',
                ROW_COLUMNS,
              )}
            >
              <span>Empresa</span>
              <span>Projeto</span>
              <span>Consultor</span>
              <span>Fase</span>
              <span>Performance</span>
              <span>Status</span>
            </div>
            <ul
              className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface"
              aria-busy={query.isFetching}
            >
              {data.items.map((org) => (
                <OrganizationRow key={org.id} org={org} />
              ))}
            </ul>
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onPage={goTo}
              busy={query.isFetching}
            />
          </>
        ) : null}
      </section>
    </PageContainer>
  );
}

/** Indicadores de topo da carteira (só das empresas acessíveis e ativas — DEC-059). */
function Summary() {
  const { client } = useAuth();
  const query = useQuery({
    queryKey: ['organizations', 'central', 'summary'],
    queryFn: ({ signal }) =>
      client.request('/organizations/summary', { schema: centralSummarySchema, signal }),
  });
  const cards: { key: keyof CentralSummary; label: string; icon: LucideIcon; alert?: boolean }[] = [
    { key: 'activeCompanies', label: 'Empresas ativas', icon: Building2 },
    { key: 'projectsInImplementation', label: 'Projetos em implantação', icon: FolderKanban },
    { key: 'projectsInMonitoring', label: 'Projetos em acompanhamento', icon: TrendingUp },
    { key: 'monitoredObjectives', label: 'Objetivos monitorados', icon: Target },
    { key: 'criticalKpis', label: 'KPIs críticos', icon: Gauge, alert: true },
    { key: 'lateActions', label: 'Ações atrasadas', icon: ListChecks, alert: true },
  ];
  return (
    <section aria-label="Indicadores da carteira" className="mt-6">
      {query.isPending ? (
        <LoadingState rows={1} label="Carregando os indicadores" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {cards.map((c) => {
            const value = query.data[c.key];
            return (
              <li
                key={c.key}
                className={
                  c.alert && value > 0
                    ? 'flex flex-col gap-1 rounded-lg border border-status-red-solid/40 bg-surface p-4'
                    : 'flex flex-col gap-1 rounded-lg border border-border bg-surface p-4'
                }
              >
                <c.icon
                  aria-hidden
                  className={
                    c.alert && value > 0 ? 'size-4 text-status-red-fg' : 'size-4 text-muted-foreground'
                  }
                />
                <span className="text-2xl font-extrabold tabular-nums">{value}</span>
                <span className="text-sm text-muted-foreground">{c.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function OrganizationRow({ org }: { org: CentralOrganization }) {
  const can = useCan();
  const project = org.activeProject;
  const base = `/empresas/${org.id}`;
  // Ações da linha (DEC-059): cada uma só para quem tem a tela de destino.
  const settings = can('administracao', 'view')
    ? `${base}/areas`
    : project && can('planejamento-estrategico', 'view')
      ? `${base}/configuracoes/performance?projeto=${project.id}`
      : null;
  const actions = [
    project && can('dashboard-executivo', 'view')
      ? { label: 'Acessar dashboard', to: `${base}/dashboards/executivo?projeto=${project.id}` }
      : null,
    can('central-empresas', 'create') && org.status === 'ACTIVE'
      ? { label: 'Abrir projeto', to: `${base}?acao=abrir-projeto` }
      : null,
    can('central-empresas', 'edit') ? { label: 'Editar cliente', to: base } : null,
    settings ? { label: 'Acessar configurações', to: settings } : null,
  ].filter((a): a is { label: string; to: string } => a !== null);
  const perf = org.performance;
  return (
    <li className={cn('grid gap-3 p-4 lg:items-center lg:gap-4 md:px-5', ROW_COLUMNS)}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="truncate text-base font-bold">
            <Link to={base} className="underline-offset-2 hover:underline">
              {org.name}
            </Link>
          </h2>
          {org.status === 'INACTIVE' ? (
            <Badge variant="neutral">{ORGANIZATION_STATUS_LABELS.INACTIVE}</Badge>
          ) : null}
        </div>
        {org.legalName ? <p className="truncate text-sm text-muted-foreground">{org.legalName}</p> : null}
      </div>
      <div className="min-w-0 text-sm">
        <p className="text-xs text-muted-foreground lg:sr-only">Projeto</p>
        {project ? (
          <>
            <p className="truncate font-semibold">{project.name}</p>
            <p className="text-muted-foreground">
              {project.horizonStart}–{project.horizonEnd}
              {org.activeProjects > 1
                ? ` · +${org.activeProjects - 1} projeto${org.activeProjects > 2 ? 's' : ''} ativo${org.activeProjects > 2 ? 's' : ''}`
                : ''}
            </p>
          </>
        ) : (
          <p className="text-muted-foreground">Sem projeto BSC ativo</p>
        )}
      </div>
      <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
        <UserRound className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 break-words">
          <span className="text-xs lg:sr-only">Consultor: </span>
          {org.consultant?.name ?? 'Sem consultor definido'}
        </span>
      </p>
      <p className="text-sm">
        <span className="text-xs text-muted-foreground lg:sr-only">Fase: </span>
        {project ? PROJECT_PHASE_LABELS[project.phase] : <span className="text-muted-foreground">—</span>}
      </p>
      <p className="text-sm">
        <span className="text-xs text-muted-foreground lg:sr-only">Performance: </span>
        {perf ? (
          <>
            <span className="font-bold tabular-nums">{formatDecimal(perf.value, 1)}%</span>{' '}
            <span className="text-xs text-muted-foreground">
              {targetPeriodLabel('MONTH', perf.month).toLowerCase()}/{perf.year}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </p>
      <div>
        <span className="text-xs text-muted-foreground lg:sr-only">Status: </span>
        <StatusBadge status={org.projectStatus} label={STATUS_LABELS[org.projectStatus]} />
      </div>
      {actions.length ? (
        <nav
          aria-label={`Ações de ${org.name}`}
          className="flex flex-wrap gap-x-4 gap-y-1 text-sm lg:col-span-full"
        >
          {actions.map((a) => (
            <Link
              key={a.label}
              to={a.to}
              className="inline-flex min-h-8 items-center font-medium text-brand-strong underline underline-offset-2 max-md:min-h-11 dark:text-brand"
            >
              {a.label}
            </Link>
          ))}
        </nav>
      ) : null}
    </li>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPage,
  busy,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
  busy: boolean;
}) {
  return (
    <nav aria-label="Paginação" className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
      <p className="text-muted-foreground" aria-live="polite">
        {total === 1 ? '1 empresa' : `${total} empresas`}
        {totalPages > 1 ? ` · página ${page} de ${totalPages}` : null}
      </p>
      {totalPages > 1 ? (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || busy} onClick={() => onPage(page - 1)}>
            <ChevronLeft aria-hidden />
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || busy}
            onClick={() => onPage(page + 1)}
          >
            Próxima
            <ChevronRight aria-hidden />
          </Button>
        </div>
      ) : null}
    </nav>
  );
}
